/*
 * The product tour's content, kept apart from the driver so the words can be
 * tested against the surfaces they point at. Stops follow the job (ask, decide,
 * organise), then open that page. `anchors` are tried in order at run time: the
 * content on the page, else the menubar control that is always there.
 */
import { NEW_SESSION_PATH } from "@/lib/new-session-route";

export type TourStop = {
  id: string;
  title: string;
  /** HTML allowed (driver.js renders it); keep to <b>, <kbd> and one <a>. */
  description: string;
  anchors: readonly string[];
  /** Workspace path to open before highlighting this stop. */
  href?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
};

export type TourAudience = {
  inboxVisible: boolean;
  /** Nothing connected and no channels yet: the tour shows the first steps
   * instead of an Inbox with nothing in it. */
  newOrganisation?: boolean;
  /** The tools connected for this organisation, by name ("Xero", "Google Docs"), which Twin answers from. */
  connectedTools?: readonly string[];
};

export const TOUR_DOCS_URL = "https://docs.mlx.systems/docs/web-app";

/** "your channels, Xero and Google Docs": what Twin can see, in words a newcomer recognises. */
export function tourSources(tools: readonly string[] = []): string {
  const all = ["your channels", ...tools];
  return all.length < 2 ? all[0]! : `${all.slice(0, -1).join(", ")} and ${all.at(-1)}`;
}

export function buildTourStops(audience: TourAudience): TourStop[] {
  const stops: TourStop[] = [
    {
      id: "ask",
      title: "Ask Twin anything",
      description: `Ask in your own words. Twin answers from ${tourSources(audience.connectedTools)}, and shows where each answer came from. <kbd>N</kbd>&nbsp;<kbd>T</kbd> asks from anywhere; <kbd>Esc</kbd> ends this tour.`,
      anchors: ['[data-tour="composer"]', '[data-tour="menu-agent"]'],
      href: NEW_SESSION_PATH,
      side: "top",
      align: "center",
    },
  ];
  if (audience.newOrganisation) {
    stops.push({
      id: "setup",
      title: "Your first steps",
      description:
        "Connect a service so your Twin can answer from your data, then create a channel for an area of your work. Each step ticks off as you go.",
      anchors: ['[data-tour="setup-checklist"]', '[data-tour="menu-sync"]'],
      href: "/workspace/home",
      side: "bottom",
      align: "start",
    });
  } else if (audience.inboxVisible) {
    stops.push({
      id: "decide",
      title: "Twin asks before it acts",
      description:
        "When something needs a person, such as two records that look like the same supplier or a bill to sign off, Twin asks you here. Accept, decline, or leave it for later. Mentions of you land here too.",
      anchors: ['[data-tour="inbox-list"]', '[data-tour="menu-inbox"]'],
      href: "/workspace/inbox",
      side: "right",
      align: "start",
    });
  }
  stops.push({
    id: "organise",
    title: "Channels hold the work",
    description: `Each channel is one area of your work, such as a project, finance or clients, with its tables, files and conversation. The <a href="${TOUR_DOCS_URL}" target="_blank" rel="noreferrer">workspace guide</a>, also under Help, explains the rest.`,
    anchors: ['[data-tour="channels-list"]', '[data-tour="menu-channels"]'],
    href: "/workspace/channels",
    side: "top",
    align: "start",
  });
  return stops;
}

/** Every anchor selector a stop can point at, for parity tests. */
export function tourAnchorSelectors(): string[] {
  const all = [
    ...buildTourStops({ inboxVisible: true }),
    ...buildTourStops({ inboxVisible: true, newOrganisation: true }),
  ];
  return [...new Set(all.flatMap((stop) => stop.anchors))];
}
